import { HttpParams } from '@angular/common/http';

type QueryValue = string | number | boolean;
type QueryValues = Record<string, QueryValue[]>;

export interface Collection<CollectionProjection> {
  entities: CollectionProjection[];
}

export interface PageMetadata {
  size: number;
  totalElements: number;
  totalPages: number;
  number: number;
}

export interface PagingCollection<CollectionProjection> extends Collection<CollectionProjection> {
  page: PageMetadata;
}

export class QueryState {
  protected values: QueryValues;

  constructor(values?: QueryValues) {
    this.values = values ?? {};
  }

  getValues(): QueryValues {
    return JSON.parse(JSON.stringify(this.values));
  }

  add(key: string, value: QueryValue) {
    if (!this.values[key]) {
      this.values[key] = [value];
    } else {
      this.values[key].push(value);
    }
  }

  setSingular(key: string, value: QueryValue) {
    this.values[key] = [value];
  }

  setMany(key: string, value: QueryValue[]) {
    this.values[key] = value;
  }

  toHttpParams() {
    const normalizedValues: QueryValues = {};
    for (const key in this.values)
      normalizedValues[key] = this.values[key]
        .map((value) => (typeof value === 'string' ? value.trim() : value))
        .filter((value) => typeof value !== 'string' || value);
    return new HttpParams().appendAll(normalizedValues);
  }

  remove(key: string, value?: QueryValue) {
    if (!value) {
      delete this.values[key];
    } else {
      this.values[key] = this.values[key].filter((val) => val != value);
    }
  }

  asString() {
    return JSON.stringify(this.getValues());
  }

  deepCopy() {
    return QueryState.fromString(this.asString());
  }

  with(key: string, value: QueryValue) {
    const newState = this.deepCopy();
    newState.add(key, value);
    return newState;
  }

  without(key: string, value?: QueryValue) {
    const newState = this.deepCopy();
    newState.remove(key, value);
    return newState;
  }

  public static fromString(s?: string) {
    if (!s) {
      return new QueryState();
    }
    return new QueryState(JSON.parse(s));
  }
}
